import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { AccountDropdown } from '@/components/account';

export interface NavigationConfig {
  show: boolean;
  showLogo?: boolean;
  showBackButton?: boolean;
  backButtonProps?: {
    onClick: () => void;
    title?: string;
  };
  title?: string;
  subtitle?: string;
  showBranding?: boolean;
  brandingText?: string;
  showLogout?: boolean;
  showAccountDropdown?: boolean;
  rightContent?: React.ReactNode;
  className?: string;
}

interface NavigationBarProps {
  config: NavigationConfig;
}

/**
 * NavigationBar - Configurable navigation bar component
 * 
 * Features:
 * - Configurable visibility (show/hide entire nav bar)
 * - Optional logo display
 * - Optional back button with custom action
 * - Optional title and subtitle
 * - Optional branding text (e.g., "Qodrix Menu")
 * - Optional logout button
 * - Custom right content slot
 * - Apple-inspired design consistent with existing UI
 */
export const NavigationBar: React.FC<NavigationBarProps> = ({ config }) => {
  const navigate = useNavigate();

  // Don't render if navigation is disabled
  if (!config.show) {
    return null;
  }

  const handleBackClick = () => {
    if (config.backButtonProps?.onClick) {
      config.backButtonProps.onClick();
    } else {
      navigate(-1); // Default back behavior
    }
  };

  return (
    <header className={`sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm ${config.className || ''}`}>
      <div className="max-w-12xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Left Section */}
          <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
            {/* Back Button */}
            {config.showBackButton && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackClick}
                leftIcon={<ChevronLeft className="w-6 h-6" />}
                className="p-2 flex-shrink-0"
                title={config.backButtonProps?.title || "Go Back"}
              />
            )}
            
            {/* Logo */}
            {config.showLogo && (
              <div className="h-8 w-8 sm:h-9 sm:w-9 flex items-center justify-center flex-shrink-0">
                <img 
                  src="/logo.svg" 
                  alt="Qodrix Menu" 
                  className="h-8 w-8 sm:h-9 sm:w-9 object-contain"
                />
              </div>
            )}
            
            {/* Branding Text */}
            {config.showBranding && config.brandingText && (
              <div className="flex-shrink-0">
                <span className="text-lg sm:text-xl font-semibold text-primary">
                  {config.brandingText}
                </span>
              </div>
            )}
            
            {/* Title and Subtitle */}
            {(config.title || config.subtitle) && (
              <div className="min-w-0 flex-1">
                {config.title && (
                  <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                    {config.title}
                  </h1>
                )}
                {config.subtitle && (
                  <p className="text-sm text-gray-600 hidden sm:block truncate">
                    {config.subtitle}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Right Section */}
          <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
            {/* Custom Right Content */}
            {config.rightContent}
            
            {/* Account Dropdown */}
            {config.showAccountDropdown && (
              <AccountDropdown />
            )}
            
            {/* Legacy Logout Button - kept for backward compatibility */}
            {config.showLogout && !config.showAccountDropdown && (
              <Button
                variant="ghost"
                onClick={() => {
                  // Legacy logout handling - should use AccountDropdown instead
                  console.warn('Legacy logout button used. Consider using AccountDropdown instead.');
                }}
                title="Sign Out"
                className="p-2"
                leftIcon={<div className="w-4 h-4 sm:w-5 sm:h-5">⚠️</div>}
              />
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default NavigationBar;